/**
 * Settings → AI & integrations → AI usage (fieldiq-api 0583).
 *
 *   1. This month's AI spend on FieldIQ's key against the company's monthly
 *      allowance (default US$10, set by FieldIQ). The meter is the API's
 *      `countedUsd / capUsd`, never a figure of its own. Calls that could not
 *      be priced are listed apart, as the API counts them.
 *   2. The company's own Anthropic API key. Write-only: the page sends it once
 *      and only ever shows the last 4 characters the API returns. Admin only,
 *      as the API is (PUT/DELETE /ai-settings/key are strict admin).
 *
 * Cross-vertical: the cap and the key are per tenant, not per vertical.
 */
import { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useAiBudget, useRemoveAiKey, useSetAiKey } from '../../hooks/useEwAiQuotes';
import { formatInstantDay } from '../../lib/calendar-date';
import { ewRefusal } from '../../lib/earthworks';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { fmtCurrency } from '../../lib/display-labels';

const PANEL = 'rounded-fi-panel border border-fieldiq-border bg-fieldiq-card';
const EYEBROW = 'text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted';
const INPUT = 'h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none';
/** AI usage is always billed in US dollars, whatever the tenant's currency. */
const usd = (n: number | null | undefined) => fmtCurrency(n, 'USD');

export default function AiUsagePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const q = useAiBudget();
  const b = q.data ?? null;
  const setKey = useSetAiKey();
  const removeKey = useRemoveAiKey();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  if (queryLoadFailure(q)) return <LoadFailureNotice error={queryFailureError(q)} subject="AI usage" />;

  const pct = b && b.capUsd > 0 ? Math.min(100, Math.round((b.countedUsd / b.capUsd) * 100)) : b?.atCap ? 100 : 0;
  const monthLabel = b ? formatInstantDay(b.monthStart, b.timeZone, { month: 'long', year: 'numeric' }) ?? '—' : '—';

  const save = async () => {
    setError(null);
    try {
      const out = await setKey.mutateAsync(draft);
      setDraft('');
      toast.success(`Key saved, ending ${out?.key.last4 ?? '—'}.`);
    } catch (e) {
      setDraft('');
      setError(ewRefusal(e, 'The key could not be saved.'));
    }
  };

  return (
    <div className="space-y-4" data-testid="ai-usage-page">
      <section className={`${PANEL} p-5`} data-testid="ai-usage-meter">
        <p className={EYEBROW}>AI usage · {monthLabel}</p>
        <h2 className="mt-1 text-lg font-bold text-white">This month on FieldIQ's key</h2>
        {q.isLoading ? (
          <span role="status" aria-label="Loading AI usage" className="mt-3 inline-block h-4 w-40 animate-pulse rounded bg-fieldiq-border" />
        ) : (
          <>
            <p className="mt-3 font-mono text-2xl font-extrabold text-white" data-testid="ai-usage-figure">
              {usd(b?.countedUsd)} <span className="text-base font-semibold text-fieldiq-muted">of {usd(b?.capUsd)}</span>
            </p>
            <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-fieldiq-dark" role="meter" aria-label="AI allowance used"
              aria-valuemin={0} aria-valuemax={b?.capUsd ?? 0} aria-valuenow={b?.countedUsd ?? 0}>
              <div className={`h-full ${b?.atCap ? 'bg-fi-signal-red' : pct >= 80 ? 'bg-fi-amber' : 'bg-fi-green'}`} style={{ width: `${pct}%` }} />
            </div>
            <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
              <div><dt className={EYEBROW}>Remaining</dt><dd className="font-mono text-white">{usd(b?.remainingUsd)}</dd></div>
              <div><dt className={EYEBROW}>AI calls</dt><dd className="font-mono text-white">{b ? b.calls : '—'}</dd></div>
              <div>
                <dt className={EYEBROW}>Not priced</dt>
                <dd className="font-mono text-white">{b ? b.unpricedCalls : '—'}</dd>
              </div>
            </dl>
            {b?.atCap && (
              <p role="alert" className="mt-3 rounded-fi-control border border-fi-amber/50 bg-fi-amber/10 px-3 py-2 text-sm text-fieldiq-text">
                The allowance is used up. AI quoting stops on FieldIQ's key until the 1st of next month{b.key.inUse ? '' : ', or until your own key is in use'}.
              </p>
            )}
            <p className="mt-3 text-xs text-fieldiq-muted">
              The allowance is set by FieldIQ. A call that could not be priced counts as US$0.50. Today the allowance applies to AI quoting.
            </p>
          </>
        )}
      </section>

      <section className={`${PANEL} p-5`} data-testid="ai-own-key">
        <p className={EYEBROW}>Your own key</p>
        <h2 className="mt-1 flex items-center gap-2 text-lg font-bold text-white"><KeyRound className="size-4 text-fi-amber" aria-hidden="true" />Anthropic API key</h2>
        <p className="mt-1 text-sm text-fieldiq-text">
          Calls made on your own key are billed to your Anthropic account and do not count toward the allowance. The key is encrypted when it is stored. FieldIQ never shows it again, only its last 4 characters.
        </p>
        {b?.key.set ? (
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            <span data-testid="ai-key-last4" className="font-mono text-white">sk-ant-…{b.key.last4}</span>
            {b.key.setAt && <span className="text-fieldiq-muted">saved {formatInstantDay(b.key.setAt, b.timeZone) ?? '—'}</span>}
            {!b.key.inUse && (
              <span className="rounded-fi-pill border border-fi-amber/50 px-2 py-0.5 text-xs text-fi-amber">Saved. AI calls will run on it once FieldIQ turns this on.</span>
            )}
            {isAdmin && (
              <button type="button" className="text-sm font-bold text-fi-signal-red underline" onClick={() => setConfirmRemove(true)} disabled={removeKey.isPending}>Remove key</button>
            )}
          </div>
        ) : (
          <p className="mt-3 text-sm text-fieldiq-muted">No key saved.</p>
        )}
        {isAdmin ? (
          <form className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto]" onSubmit={(e) => { e.preventDefault(); if (draft.trim()) save(); }}>
            <input type="password" autoComplete="off" spellCheck={false} className={INPUT} value={draft} onChange={(e) => setDraft(e.target.value)}
              placeholder={b?.key.set ? 'Paste a new key to replace it' : 'sk-ant-…'} aria-label="Anthropic API key" />
            <button type="submit" className="fi-action fi-action--standard fi-action--primary disabled:opacity-60" disabled={!draft.trim() || setKey.isPending}>
              {setKey.isPending ? 'Saving…' : b?.key.set ? 'Replace key' : 'Save key'}
            </button>
          </form>
        ) : (
          <p className="mt-3 text-xs text-fieldiq-muted">Only an admin can add or remove the key.</p>
        )}
        {error && <p role="alert" className="mt-2 text-sm text-fi-signal-red">{error}</p>}
      </section>

      <ConfirmModal
        isOpen={confirmRemove}
        title="Remove your Anthropic key?"
        body="AI features go back to FieldIQ's key and its monthly allowance."
        confirmLabel="Remove key"
        loading={removeKey.isPending}
        onConfirm={async () => {
          setConfirmRemove(false);
          try { await removeKey.mutateAsync(); toast.success('Key removed.'); }
          catch (e) { setError(ewRefusal(e, 'The key could not be removed.')); }
        }}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}
